import { useId, useState } from 'react';
import type { CreatePricingLevel, PricingLevel, Staff } from '@salonomy/types';
import { Button, Card, Field, Pill, Select, Spinner, TextInput } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { chairStaff } from '../../data/salon.js';
import {
  useAssignStaffLevel,
  useCreatePricingLevel,
  useDeletePricingLevel,
  usePricingLevels,
  useUpdatePricingLevel,
} from '../../data/pricing.js';
import { ApiClientError } from '../../lib/api.js';
import { useToast } from '../../lib/toast.js';

/**
 * Salon-wide pricing LEVELS (junior / senior / director …) plus the per-staff level assignment
 * (WS9 brief 09 §1). A level carries a signed `adjustmentBps` applied OVER the base price (+2000 =
 * +20%); putting a staff member on a level is the rate they charge at. Both feed the deterministic
 * price the API resolves — this panel never computes a resolved price, it only edits the inputs.
 *
 * RBAC: level CRUD + staff-level assignment are manager-only (mirror of data/pricing.ts); the host
 * (SettingsPage) renders this in the manager branch, but we accept `canEdit` to disable defensively
 * so a renter never gets a 403 as their first feedback.
 */
export function PricingLevelsPanel({
  staff,
  canEdit = true,
}: {
  staff: Staff[];
  canEdit?: boolean;
}) {
  const toast = useToast();
  const { data: levels, isLoading } = usePricingLevels();
  const createLevel = useCreatePricingLevel();
  const [editLevel, setEditLevel] = useState<{ existing?: PricingLevel } | null>(null);

  // The chairs whose rate a level governs — the same set the diary treats as bookable.
  const chairs = chairStaff(staff);

  // bps is stored signed; the editor takes a friendlier percent (+20 → +2000). Kept here so the
  // "add level" row stays a one-liner like the rest of Settings; the modal handles edits.
  const [name, setName] = useState('');
  const [percent, setPercent] = useState('0');

  async function addLevel() {
    if (!canEdit) return;
    const pct = Number(percent);
    if (!name.trim()) {
      toast.error('Give the level a name.');
      return;
    }
    if (!Number.isFinite(pct) || pct < -90 || pct > 500) {
      toast.error('Adjustment must be between -90% and +500%.');
      return;
    }
    const body: CreatePricingLevel = {
      name: name.trim(),
      adjustmentBps: Math.round(pct * 100),
      sortOrder: levels?.length ?? 0,
    };
    try {
      await createLevel.mutateAsync(body);
      toast.success(`${body.name} level added`);
      setName('');
      setPercent('0');
    } catch (err) {
      toast.error(apiMessage(err, 'Could not add this level.'));
    }
  }

  const activeLevels = (levels ?? []).filter((l) => l.active);

  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div className="section-title">Pricing levels</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Seniority rates (junior / senior / director). Each adjusts the base price up or down;
            put a stylist on a level and that’s the rate they charge at. Set a flat price for a
            specific service and level in the service’s pricing matrix.
          </div>
        </div>
        {isLoading ? <Spinner /> : <Pill tone="neutral">Brief 09</Pill>}
      </div>

      {isLoading ? null : (
        <>
          {/* ── The levels themselves ── */}
          <div className="settings-list" style={{ marginTop: 12 }}>
            {activeLevels.length === 0 ? (
              <div className="muted" style={{ fontSize: 13, padding: '4px 0' }}>
                No levels yet — every stylist charges the service’s base price. Add one below to
                price by seniority.
              </div>
            ) : (
              activeLevels.map((l) => (
                <div key={l.id} className="settings-row">
                  <div>
                    <div style={{ fontWeight: 600 }}>{l.name}</div>
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      {describeAdjustment(l.adjustmentBps)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="mono">{formatBps(l.adjustmentBps)}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditLevel({ existing: l })}
                      disabled={!canEdit}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Add a level (inline one-liner, matching Settings density) ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.6fr 1fr auto',
              gap: 10,
              alignItems: 'end',
              marginTop: 12,
            }}
          >
            <Field label="New level name">
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Director"
                disabled={!canEdit}
              />
            </Field>
            <Field label="Adjustment (%)" hint="+20 = +20%, −10 = −10%">
              <TextInput
                inputMode="decimal"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                placeholder="0"
                disabled={!canEdit}
              />
            </Field>
            <Button onClick={addLevel} disabled={!canEdit || createLevel.isPending}>
              Add level
            </Button>
          </div>

          {/* ── Per-staff level assignment ── */}
          <div className="section-title" style={{ fontSize: 15, margin: '16px 0 2px' }}>
            Who’s on each level
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 4 }}>
            A stylist with no level charges the base price. The resolved price a client pays is
            worked out by us at booking — never recomputed here.
          </div>
          {chairs.length === 0 ? (
            <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
              Add stylists to assign them a level.
            </div>
          ) : activeLevels.length === 0 ? (
            <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
              Add a level above before assigning stylists to it.
            </div>
          ) : (
            <div className="settings-list">
              {chairs.map((s) => (
                <StaffLevelRow key={s.id} staffMember={s} levels={activeLevels} canEdit={canEdit} />
              ))}
            </div>
          )}
        </>
      )}

      {editLevel ? (
        <PricingLevelModal
          existing={editLevel.existing}
          onClose={() => setEditLevel(null)}
          canEdit={canEdit}
        />
      ) : null}
    </Card>
  );
}

/** One stylist's level picker. Assignment is a PUT (idempotent); the select is the whole control. */
function StaffLevelRow({
  staffMember,
  levels,
  canEdit,
}: {
  staffMember: Staff;
  levels: PricingLevel[];
  canEdit: boolean;
}) {
  const toast = useToast();
  const assign = useAssignStaffLevel();

  // The current assignment isn't on the Staff record (it's a separate pricing fact), so we track the
  // last value we set locally and otherwise leave the select on its server-resolved default once the
  // matrix/resolve queries warm. We keep this minimal: the select reflects what the manager chooses.
  const [levelId, setLevelId] = useState<string>('');

  async function choose(next: string) {
    if (!canEdit) return;
    setLevelId(next);
    try {
      await assign.mutateAsync({ staffId: staffMember.id, levelId: next || null });
      toast.success(
        next
          ? `${staffMember.displayName} → ${levels.find((l) => l.id === next)?.name ?? 'level'}`
          : `${staffMember.displayName} now charges the base price`,
      );
    } catch (err) {
      toast.error(apiMessage(err, 'Could not set this level.'));
    }
  }

  return (
    <div className="settings-row">
      <div style={{ fontWeight: 600 }}>{staffMember.displayName}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Select
          value={levelId}
          onChange={(e) => choose(e.target.value)}
          disabled={!canEdit || assign.isPending}
          style={{ width: 180 }}
        >
          <option value="">Base price (no level)</option>
          {levels.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} ({formatBps(l.adjustmentBps)})
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

/** Create / edit one pricing level (name, % adjustment). Edit can also retire a level. */
function PricingLevelModal({
  existing,
  onClose,
  canEdit,
}: {
  existing?: PricingLevel;
  onClose: () => void;
  canEdit: boolean;
}) {
  const toast = useToast();
  const update = useUpdatePricingLevel();
  const remove = useDeletePricingLevel();
  const labelId = useId();
  const editing = Boolean(existing);

  const [name, setName] = useState(existing?.name ?? '');
  const [percent, setPercent] = useState(existing ? String(existing.adjustmentBps / 100) : '0');
  const [busy, setBusy] = useState(false);
  const [confirmRetire, setConfirmRetire] = useState(false);

  async function save() {
    if (!existing) return;
    const pct = Number(percent);
    if (!name.trim()) {
      toast.error('Give the level a name.');
      return;
    }
    if (!Number.isFinite(pct) || pct < -90 || pct > 500) {
      toast.error('Adjustment must be between -90% and +500%.');
      return;
    }
    setBusy(true);
    try {
      await update.mutateAsync({
        levelId: existing.id,
        body: { name: name.trim(), adjustmentBps: Math.round(pct * 100) },
      });
      toast.success('Level updated');
      onClose();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not save this level.'));
    } finally {
      setBusy(false);
    }
  }

  async function retire() {
    if (!existing) return;
    setBusy(true);
    try {
      await remove.mutateAsync(existing.id);
      toast.success(`${existing.name} level retired`);
      setConfirmRetire(false);
      onClose();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not retire this level.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ConfirmModal
        open={!confirmRetire}
        title={editing ? `Edit ${existing?.name}` : 'Edit level'}
        confirmLabel="Save"
        tone="primary"
        busy={busy}
        onConfirm={save}
        onClose={onClose}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 12 }}>
          <Field label="Name" htmlFor={`${labelId}-name`}>
            <TextInput
              id={`${labelId}-name`}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
          <Field label="Adjustment (%)" hint="+20 = +20%" htmlFor={`${labelId}-pct`}>
            <TextInput
              id={`${labelId}-pct`}
              inputMode="decimal"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
        </div>
        <div className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>
          {describeAdjustment(Math.round((Number(percent) || 0) * 100))}. This shifts every
          service’s price for stylists on this level, except where you’ve set a flat price for that
          service.
        </div>
        {editing && canEdit ? (
          <Button
            variant="ghost"
            onClick={() => setConfirmRetire(true)}
            disabled={busy}
            style={{ marginTop: 12, color: 'var(--slm-danger)', paddingLeft: 0 }}
          >
            Retire this level
          </Button>
        ) : null}
      </ConfirmModal>

      <ConfirmModal
        open={confirmRetire}
        title="Retire level"
        confirmLabel="Retire level"
        confirmPhrase={existing?.name}
        busy={busy}
        onConfirm={retire}
        onClose={() => setConfirmRetire(false)}
      >
        <p style={{ margin: 0 }}>
          Retiring <strong>{existing?.name}</strong> stops it pricing new bookings and unassigns
          stylists from it (they revert to the base price). Past appointments are unaffected.
        </p>
      </ConfirmModal>
    </>
  );
}

// ── Display helpers (no money math — bps is a config input, not a resolved price) ──────────

/** 2000 → "+20%", -1000 → "−10%", 0 → "±0%". */
function formatBps(bps: number): string {
  const pct = bps / 100;
  if (pct === 0) return '±0%';
  const sign = pct > 0 ? '+' : '−';
  return `${sign}${Math.abs(pct)}%`;
}

function describeAdjustment(bps: number): string {
  if (bps === 0) return 'Charges the base price';
  return bps > 0 ? `${formatBps(bps)} on the base price` : `${formatBps(bps)} off the base price`;
}

/** Surface the server's own 4xx message (422/409 etc.) faithfully; fall back for anything else. */
function apiMessage(err: unknown, fallback: string): string {
  return err instanceof ApiClientError ? err.message : fallback;
}
